
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { VirtualTeamTable } from "./VirtualTeamTable";
import { VirtualTeamDialog } from "./VirtualTeamDialog";
import { useVirtualTeam } from "../hooks/useVirtualTeam";
import { Account, VirtualTeamMember } from "../types";
import { toast } from "sonner";

interface VirtualTeamSectionProps {
  selectedAccount: Account | null;
  canEdit: boolean;
}

export const VirtualTeamSection = ({ selectedAccount, canEdit }: VirtualTeamSectionProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<VirtualTeamMember | null>(null);
  
  const { virtualTeam, isLoading, addTeamMember, updateTeamMember, deleteTeamMember } = useVirtualTeam(selectedAccount);

  const handleAddNew = () => {
    if (!canEdit) {
      toast.error("Only account owners can add team members");
      return;
    }
    setEditingMember(null);
    setIsDialogOpen(true);
  };

  const handleEdit = (member: VirtualTeamMember) => {
    if (!canEdit) {
      toast.error("Only account owners can edit team members");
      return;
    }
    setEditingMember(member);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!canEdit) {
      toast.error("Only account owners can delete team members");
      return;
    }
    await deleteTeamMember(id);
  };

  const handleSave = async (memberData: Omit<VirtualTeamMember, 'id'>) => {
    if (!canEdit) {
      toast.error("Only account owners can save team members");
      return;
    }

    let success = false;
    if (editingMember) {
      success = await updateTeamMember(editingMember.id, memberData);
    } else {
      success = await addTeamMember(memberData);
    }

    if (success) {
      setIsDialogOpen(false);
      setEditingMember(null);
    }
  };

  return (
    <Card className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-white">Virtual Account Team</CardTitle>
          <Button
            variant="outline"
            className="border-primary/20 bg-primary hover:bg-primary/90 text-black"
            onClick={handleAddNew}
            disabled={!canEdit}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Team Member
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <VirtualTeamTable
          virtualTeam={virtualTeam}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          canEdit={canEdit}
        />
        
        <VirtualTeamDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onSave={handleSave}
          editingMember={editingMember}
        />
      </CardContent>
    </Card>
  );
};
